import config from "config/index";
import addFullPageLoader from "./addFullPageLoader";
import getFormattedSourceDetails from "@Utils/getFormattedSourceDetails";
import removeFullPageLoader from "./removeFullPageLoader";
import handleZendeskWidgetPress from "./handleZendeskWidgetPress";
import emitAiChatInitiatedEvent from "./emitAiChatInitiatedEvent";
import { FLOATING_BUTTON_ENTRY_POINTS } from "constants/index";
import getParsedLocalStorageData from "@Components/LeadFormModal/helpers/getParsedLocalStorageData";
import { sendWebViewEvent } from "@Utils/webviewEvents";
import { WEB_VIEW_APP_EVENTS } from "@Utils/webApputils/analyticEvents";

type ConversationField = {
  id: string;
  value: string;
};

type UserDetails = {
  "user_details/email"?: string;
  "user_details/name"?: string;
  "user_details/phone"?: string;
};

type LeadSavedData = {
  id?: number;
  location?: {
    name: string;
    country?: Record<any, any>;
  };
  inventory_id?: {
    name: string;
  };
  inventory_root_id?: {
    name: string;
  };
};

type RecentlyVisitedItem = {
  id: string;
  canonical_name: string;
  name: string;
  region_type: string;
  country?: string;
};

declare global {
  interface Window {
    zE: ((type: string, action: string, ...args: any[]) => void) | undefined;
    zESettings?: {
      webWidget?: {
        launcher?: {
          suppress?: boolean;
        };
      };
    };
  }
}

const handleLoadChatWidget = (isMobile = true, conversationFields: ConversationField[] = []) =>
  new Promise((resolve, reject) => {
    if (config.ZENDESK_IS_VISIBLE !== "true") {
      resolve(undefined);
      return;
    }

    const existingScript = document.querySelector("script#ze-snippet");
    if (existingScript && existingScript.parentNode) {
      existingScript.parentNode.removeChild(existingScript);
    }

    if (config.IS_CHINA_APP) {
      window.zESettings = {
        webWidget: {
          launcher: {
            suppress: true, // This hides the default launcher button
          },
        },
      };
    }

    const script = document.createElement("script");
    script.id = "ze-snippet";
    script.src = config.ZENDESK_URL || "";
    script.defer = true;
    script.nonce = (window as any)?.__INITIAL_STATE__?.appData?.nonce || "";
    if (isMobile)
      addFullPageLoader(() => {
        // @ts-ignore
        zE("messenger", "close");
      });

    // @ts-ignore
    script.onload = () => {
      // @ts-ignore
      if (typeof zE !== "undefined") {
        // @ts-ignore
        resolve();
        // Initialize the messenger with empty conversation fields initially
        // @ts-ignore
        zE("messenger:set", "conversationFields", []);

        // @ts-ignore
        zE("messenger:on", "open", () => {
          const sourceDetails = getFormattedSourceDetails();
          const details = getDetails();
          const zendeskUtmParamsId = "19799472549777";
          const zendeskLeadId = "27145654344209";
          const zendeskEmail = "27157736713361";
          const zendeskName = "17936387423761";
          const zendeskPhone = "17943590478225";
          const zendeskCity = "27182128370705";
          const zendeskLeadInventoryId = "27189645882129";
          const zendeskLeadInventoryRootId = "27188893346449";
          const zendeskIsLoggedIn = "27404981002385";
          const zendeskUniversity = "25462984926609";
          const zendeskForceChat = "28398632699793";

          // Reset conversationFields with live data each time the widget is opened
          // @ts-ignore
          zE("messenger:set", "conversationFields", [
            { id: zendeskUtmParamsId, value: JSON.stringify(sourceDetails) },
            { id: zendeskLeadId, value: details.id },
            { id: zendeskEmail, value: details.email },
            { id: zendeskName, value: details.name },
            { id: zendeskPhone, value: details.phone },
            { id: zendeskCity, value: details.city },
            { id: zendeskLeadInventoryId, value: details.inventoryId },
            { id: zendeskLeadInventoryRootId, value: details.inventoryRootId },
            { id: zendeskIsLoggedIn, value: details.isLoggedIn },
            { id: zendeskUniversity, value: details.university },
            { id: zendeskForceChat, value: "true" },
            ...conversationFields,
          ]);
          sendWebViewEvent(WEB_VIEW_APP_EVENTS.ZENDESK_TOGGLE, {
            isOpened: true,
          });
          handleZendeskWidgetPress();
          emitAiChatInitiatedEvent(FLOATING_BUTTON_ENTRY_POINTS.PAGE_BUTTON, "zendesk_chatbot");
        });

        // @ts-ignore
        zE("messenger:on", "close", () => {
          removeFullPageLoader();
          sendWebViewEvent(WEB_VIEW_APP_EVENTS.ZENDESK_TOGGLE, {
            isOpened: false,
          });
        });
      } else {
        removeFullPageLoader();
        reject(new Error("zE is undefined"));
      }
    };

    script.onabort = () => {
      removeFullPageLoader();
      reject(new Error("aborted!"));
    };

    if (document) {
      document.body.appendChild(script);
    }
  });

const getDetails = () => {
  const leadSavedData =
    (getParsedLocalStorageData("lead-form-one-success-data") as LeadSavedData) || null;
  const recentlyVisitedRaw = getParsedLocalStorageData("recentlyVisited");
  const recentlyVisited =
    Array.isArray(recentlyVisitedRaw) && recentlyVisitedRaw.length > 0
      ? (recentlyVisitedRaw as RecentlyVisitedItem[])
      : null;

  const localStorageData = getParsedLocalStorageData(
    "lead-form-one-saved-data",
  ) as UserDetails | null;

  // @ts-ignore
  const isLoggedIn = window?.__INITIAL_STATE__?.appData?.isUserLoggedIn || false;
  const latestLocality = recentlyVisited?.filter((item) => item.region_type === "locality").pop();
  const university =
    recentlyVisited?.filter((item) => item.region_type === "establishment").pop()?.name || "";

  const country = leadSavedData?.location?.country?.long_name || latestLocality?.country || "";
  const city = [leadSavedData?.location?.name || latestLocality?.name, country]
    .filter(Boolean)
    .join(", ");
  const details = {
    id: leadSavedData?.id?.toString() || "",
    city,
    university,
    inventoryId: leadSavedData?.inventory_id?.toString() || "",
    inventoryRootId: leadSavedData?.inventory_root_id?.toString() || "",
    email: localStorageData?.["user_details/email"] || "",
    name: localStorageData?.["user_details/name"] || "",
    phone: localStorageData?.["user_details/phone"]?.toString() || "",
    isLoggedIn: isLoggedIn ? "true" : "false",
  };

  return details;
};

export default handleLoadChatWidget;
